/**
 * Forensic-accountant registration — court-exhibit auth shell wrapping the
 * hand-built JWT register form. Invite-only (audit C-5): a valid ?invite= token
 * is required; without it the form shows an invite-required notice and blocks.
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import AuthLayout from '../components/auth/AuthLayout';
import { AuthField, AuthSubmit, AuthError, AuthNotice } from '../components/auth/authForm';
import { BackToSite } from './AccountantLogin';

export default function AccountantRegister() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || '';
  const invitedEmail = searchParams.get('email') || '';

  const [formData, setFormData] = useState({
    email: invitedEmail,
    password: '',
    confirmPassword: '',
    full_name: '',
    firm_name: '',
    license_number: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAccountantAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!inviteToken) {
      setError('Missing invite token. Registration is invite-only — please follow the link from your invite email.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        firm_name: formData.firm_name,
        license_number: formData.license_number || undefined,
        phone: formData.phone || undefined,
        invite_token: inviteToken,
      });
      // External attesters land on /accountant/home (Phase 08).
      navigate('/accountant/home');
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Set up your forensic-accountant access to review and attest shared reports."
      altPrompt="Already have an account?"
      altLinkText="Sign in"
      altLinkTo="/accountant/login"
      framed={false}
      footerExtra={<BackToSite />}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {!inviteToken && (
          <AuthNotice title="Invite required">
            Registration is invite-only. Follow the link from your invite email, or ask the firm that
            shared the report to send one.
          </AuthNotice>
        )}
        <AuthError>{error}</AuthError>

        <AuthField id="full_name" name="full_name" label="Full name" value={formData.full_name} onChange={handleChange} required placeholder="John Doe, CPA" autoComplete="name" />
        <AuthField id="email" name="email" label="Email address" type="email" value={formData.email} onChange={handleChange} required placeholder="accountant@firm.com" autoComplete="email" />
        <AuthField id="firm_name" name="firm_name" label="Firm name" value={formData.firm_name} onChange={handleChange} required placeholder="ABC Forensic Accounting" autoComplete="organization" />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <AuthField id="license_number" name="license_number" label="License no. (optional)" value={formData.license_number} onChange={handleChange} placeholder="CPA-12345" />
          <AuthField id="phone" name="phone" label="Phone (optional)" type="tel" value={formData.phone} onChange={handleChange} placeholder="+1-555-0100" autoComplete="tel" />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <AuthField id="password" name="password" label="Password" type="password" value={formData.password} onChange={handleChange} required placeholder="••••••••" autoComplete="new-password" />
          <AuthField id="confirmPassword" name="confirmPassword" label="Confirm password" type="password" value={formData.confirmPassword} onChange={handleChange} required placeholder="••••••••" autoComplete="new-password" />
        </div>

        <AuthSubmit loading={loading} loadingText="Creating account…">
          Create account
        </AuthSubmit>
      </form>
    </AuthLayout>
  );
}
